/**
 * Builds the default document title for approval letter uploads so
 * fulfillment users don't have to type it from scratch.
 *
 * Title format:
 *   `[Payer] - Auth approval - [J-code] [Drug name] // [Start] - [End] - [Visits] visits`
 * e.g. `UHC - Auth approval - J9316 Phesgo // 06-23-2026 - 06-23-2027 - 999 visits`
 *
 * @module narLetterModal/utils/generateApprovalLetterDocumentTitle
 */

import {
  AuthOnFile,
  JCodeDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { getAllJCodesFromAuthOnFile } from "../../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { formatDate } from "../../../../utils/formatDate";

/** Date format used in OncoEMR-style approval letter titles (e.g. `06-23-2026`). */
const APPROVAL_LETTER_DATE_FORMAT = "MM-DD-YYYY";

/** Fixed label segment shared by every approval letter title. */
const AUTH_APPROVAL_LABEL = "Auth approval";

/**
 * Resolves the drug name for a J-code, preferring `medicine_name` and
 * falling back to the generic `description`.
 *
 * @param jCode - The J-code detail record.
 * @returns The drug name, or an empty string when unavailable.
 */
const resolveDrugName = (jCode: JCodeDetails): string =>
  (jCode.medicine_name || jCode.description || "").trim();

/**
 * Resolves the units/visits portion of the title.
 *
 * Prefers an explicit visit count (`num_visits`, falling back to the
 * auth-letter agent's `num_visits` / `calculated_visits`) rendered as
 * `"<n> visits"`. When only `units_approved` is available it is used verbatim.
 *
 * @param jCode - The J-code detail record.
 * @returns The visits/units label, or an empty string when unavailable.
 */
const resolveVisitsLabel = (jCode: JCodeDetails): string => {
  const visits =
    jCode.num_visits ??
    jCode.auth_letter?.num_visits ??
    jCode.auth_letter?.calculated_visits;

  if (visits !== undefined && visits !== null && `${visits}`.trim() !== "") {
    return `${visits} visits`;
  }

  const unitsApproved = jCode.auth_letter?.units_approved?.trim();
  return unitsApproved ?? "";
};

/**
 * Builds the per-J-code segment of the title:
 * `[J-code] [Drug name] // [Start] - [End] - [Visits] visits`.
 *
 * Missing pieces are omitted so a partially populated J-code still yields a
 * clean, human-readable segment.
 *
 * @param jCode - The J-code detail record.
 * @returns The formatted segment, or an empty string when nothing is known.
 */
const buildJCodeSegment = (jCode: JCodeDetails): string => {
  const startDate = formatDate(
    jCode.start_date || jCode.auth_letter?.auth_letter_start_date,
    APPROVAL_LETTER_DATE_FORMAT,
  );
  const endDate = formatDate(
    jCode.end_date || jCode.auth_letter?.auth_letter_end_date,
    APPROVAL_LETTER_DATE_FORMAT,
  );
  const drugName = resolveDrugName(jCode);
  const visitsLabel = resolveVisitsLabel(jCode);

  const codeAndDrug = [jCode.j_code?.trim(), drugName]
    .filter(Boolean)
    .join(" ");
  const dateAndVisits = [startDate, endDate, visitsLabel]
    .filter(Boolean)
    .join(" - ");

  if (codeAndDrug && dateAndVisits) {
    return `${codeAndDrug} // ${dateAndVisits}`;
  }
  return codeAndDrug || dateAndVisits;
};

/**
 * Generates the default approval letter document title from the payer
 * name and the selected J-codes.
 *
 * @param payerName - The payer name shown in the title's leading segment.
 * @param jCodes - The selected J-code detail records to describe.
 * @returns The pre-fill title, or an empty string when nothing can be built.
 * @example
 * generateApprovalLetterDocumentTitle("UHC", [jCode])
 * // "UHC - Auth approval - J9316 Phesgo // 06-23-2026 - 06-23-2027 - 999 visits"
 */
export const generateApprovalLetterDocumentTitle = (
  payerName: string,
  jCodes: JCodeDetails[],
): string => {
  const segments = jCodes.map(buildJCodeSegment).filter(Boolean);
  if (segments.length === 0) {
    return "";
  }

  const prefix = [payerName?.trim(), AUTH_APPROVAL_LABEL]
    .filter(Boolean)
    .join(" - ");

  return `${prefix} - ${segments.join(", ")}`;
};

/**
 * Resolves the {@link JCodeDetails} for a set of selected J-code identifiers
 * from the auth-on-file payload, preserving the selection order.
 *
 * @param authOnFile - The auth-on-file record holding the J-code entries.
 * @param selectedJCodeIds - Selected J-code identifiers (`JCodeDetails.id`, falling back to `j_code`).
 * @returns The matching J-code detail records (selection order), empty when none match.
 */
export const resolveSelectedJCodeDetails = (
  authOnFile: AuthOnFile | null | undefined,
  selectedJCodeIds: string[],
): JCodeDetails[] => {
  if (!selectedJCodeIds.length) {
    return [];
  }
  const allJCodes = getAllJCodesFromAuthOnFile(authOnFile ?? undefined) ?? [];
  return selectedJCodeIds
    .map((id) => allJCodes.find((jCode) => (jCode.id ?? jCode.j_code) === id))
    .filter((jCode): jCode is JCodeDetails => jCode != null);
};
